/**
 * The live transcript's bookkeeping (PRD 66): lines pushed to the page the
 * moment they are written, and reads of the whole transcript, agreeing.
 */

/** What a line must carry for these — the contract's line has more. */
export interface Sequenced {
  id: string;
  /** Its place in the transcript, as a decimal string (a bigint on the server). */
  seq: string;
}

/** One line into what the page holds, once: replaced if it is there, added if not. */
export function withLine<L extends Sequenced>(held: L[], line: L): L[] {
  const at = held.findIndex((l) => l.id === line.id);
  if (at === -1) return [...held, line];
  const next = [...held];
  next[at] = line;
  return next;
}

/**
 * A fresh read of the whole transcript, and the pushed lines it was too early
 * to include.
 *
 * ⚠ A line pushed while a read was on its way is newer than anything in the
 * read's answer, and taking the answer alone would take that line off the
 * screen until the read after. A pushed line older than the answer's newest
 * and missing from it is gone from the server — the recording's own
 * transcript replaced it — and stays gone.
 */
export function withNewer<L extends Sequenced>(fresh: L[], pushed: L[]): L[] {
  const newest = fresh.reduce((max, l) => (BigInt(l.seq) > max ? BigInt(l.seq) : max), -1n);
  const known = new Set(fresh.map((l) => l.id));
  return [...fresh, ...pushed.filter((l) => !known.has(l.id) && BigInt(l.seq) > newest)];
}
